import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, Loader2, Lock, Sparkles, Zap, Crown, ArrowRight, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TIER_META = {
  starter: {
    icon: Sparkles,
    color: "#3377FF",
    tagline: "For solo founders",
    perks: [
      "1 project",
      "50 SEO audits / month",
      "20 AI blog generations / month",
      "Autopilot ON",
      "GSC + GA4 analytics",
    ],
  },
  pro: {
    icon: Zap,
    color: "#10B981",
    tagline: "For growth teams",
    badge: "Most popular",
    perks: [
      "5 projects",
      "Unlimited audits",
      "Unlimited AI blogs",
      "Autopilot ON",
      "Site Explorer (competitor analysis)",
      "Real keyword DB (SerpAPI + DataForSEO)",
      "GSC-driven auto blog suggestions",
    ],
  },
  agency: {
    icon: Crown,
    color: "#F59E0B",
    tagline: "For agencies",
    perks: [
      "25 projects",
      "Everything in Pro",
      "White-label / custom domain",
      "Multi-user / multi-client",
      "Priority support",
      "Onboarding call",
    ],
  },
};

export default function UpgradePage() {
  const { user, access } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);

  useEffect(() => {
    api.get("/billing/tiers").then(({ data }) => {
      setTiers(data.tiers || []);
    }).catch(() => {}).finally(() => setLoading(false));
    if (params.get("cancelled") === "1") {
      toast.info("Checkout cancelled. Pick a plan when you're ready.");
    }
  }, [params]);

  const checkout = async (tierId) => {
    if (!user) return navigate("/login");
    setCheckingOut(tierId);
    try {
      const { data } = await api.post("/billing/checkout", {
        tier: tierId,
        origin_url: window.location.origin,
      });
      // Redirect to Stripe
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setCheckingOut(null);
    }
  };

  const isLocked = access?.locked === true;
  const isTrial = access?.status === "trial";

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100" data-testid="upgrade-page">
      {/* Header */}
      <header className="border-b border-zinc-800 px-6 py-4 flex items-center justify-between sticky top-0 bg-[#09090B]/95 backdrop-blur z-10">
        <Link to="/dashboard" className="font-display text-xl font-semibold tracking-tight" data-testid="upgrade-logo">RankPilot</Link>
        <div className="flex items-center gap-3 text-sm text-zinc-400">
          {user && <span data-testid="upgrade-user-email">{user.email}</span>}
          <Link to="/account" className="text-[#3377FF] hover:text-white transition-colors" data-testid="upgrade-account-link">Account</Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-12 space-y-12">
        {/* Lock banner */}
        {isLocked && (
          <div className="border border-red-900/40 bg-red-950/30 rounded-sm p-5 flex items-start gap-4" data-testid="upgrade-lock-banner">
            <div className="w-10 h-10 rounded-sm bg-red-500/15 border border-red-500/40 grid place-items-center flex-shrink-0">
              <Lock className="w-5 h-5 text-red-400" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-lg font-semibold">Your 3-day trial has ended</h2>
              <p className="text-sm text-zinc-400 mt-1 leading-relaxed">
                All features are locked until you choose a plan. Your data is safe — pick a plan below and pick up exactly where you left off.
              </p>
            </div>
            <ShieldAlert className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
          </div>
        )}

        {isTrial && (
          <div className="border border-[#10B981]/30 bg-[#10B981]/5 rounded-sm p-4 flex items-center gap-3" data-testid="upgrade-trial-banner">
            <Sparkles className="w-5 h-5 text-[#10B981] flex-shrink-0" />
            <div className="text-sm text-zinc-300">
              <strong className="text-[#10B981]">{access.days_left} {access.days_left === 1 ? "day" : "days"} left</strong> in your free trial. Upgrade anytime to keep your momentum after it ends.
            </div>
          </div>
        )}

        {/* Title */}
        <div className="text-center space-y-3">
          <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-400 text-[10px] tracking-widest uppercase">Pricing</Badge>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">Stop guessing. Start ranking.</h1>
          <p className="text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            Every plan includes Autopilot, real GSC + GA4 data, AI blogs with images, and our growing 13-CMS integration network. Cancel anytime.
          </p>
        </div>

        {/* Pricing grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#0055FF]" /></div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-5" data-testid="upgrade-tier-grid">
            {tiers.map((t) => {
              const meta = TIER_META[t.id];
              const Icon = meta?.icon || Sparkles;
              const isPopular = t.id === "pro";
              const isBusy = checkingOut === t.id;
              return (
                <div
                  key={t.id}
                  className={`relative border rounded-sm p-7 transition-all ${
                    isPopular
                      ? "border-[#10B981] bg-gradient-to-b from-[#10B981]/10 to-transparent lg:-translate-y-3"
                      : "border-zinc-800 bg-zinc-950/40 hover:border-zinc-700"
                  }`}
                  data-testid={`tier-card-${t.id}`}
                >
                  {meta?.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="rounded-sm bg-[#10B981] text-[#062D24] border-0 text-[10px] tracking-widest uppercase">{meta.badge}</Badge>
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-sm grid place-items-center" style={{ background: `${meta?.color}15`, border: `1px solid ${meta?.color}40` }}>
                      <Icon className="w-5 h-5" style={{ color: meta?.color }} />
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-semibold tracking-tight">{t.name}</h3>
                      <p className="text-xs text-zinc-500">{meta?.tagline}</p>
                    </div>
                  </div>
                  <div className="my-6">
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-display text-4xl font-semibold tracking-tight">${t.price_usd}</span>
                      <span className="text-sm text-zinc-500">/ month</span>
                    </div>
                    <p className="text-xs text-zinc-600 mt-1">Billed every 30 days. Cancel anytime.</p>
                  </div>
                  <ul className="space-y-2.5 mb-7">
                    {(meta?.perks || []).map((p) => (
                      <li key={p} className="flex items-start gap-2.5 text-sm text-zinc-300">
                        <Check className="w-4 h-4 text-[#10B981] flex-shrink-0 mt-0.5" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => checkout(t.id)}
                    disabled={isBusy}
                    className={`w-full rounded-sm h-11 ${
                      isPopular ? "bg-[#10B981] hover:bg-[#10B981]/90 text-[#062D24]" : "bg-[#0055FF] hover:bg-[#3377FF]"
                    }`}
                    data-testid={`upgrade-checkout-${t.id}`}
                  >
                    {isBusy ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting to Stripe…</>
                    ) : (
                      <>Choose {t.name} <ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        <div className="text-center text-xs text-zinc-600 space-y-1 pt-6 border-t border-zinc-800">
          <p>Secure payments by Stripe. We never store your card.</p>
          <p>Questions? <a href="mailto:support@myrankpilotai.com" className="text-[#3377FF] hover:underline" data-testid="upgrade-contact">support@myrankpilotai.com</a></p>
        </div>
      </div>
    </div>
  );
}
